import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../config/ConfigContext';
import { useSupabaseAuth } from '../providers/SupabaseAuthProvider';
import { supabase } from '../lib/supabaseClient';
import { isCommunityFeatureEnabled } from '../utils/featureHelpers';
import OTPVerificationModal from '../components/services/OTPVerificationModal';
import taxDocsService from '../services/taxDocsService';
import '../styles/taxDocs.css';

const TaxDocsPage = () => {
  const { config, theme } = useConfig();
  const { session, user, signOut } = useSupabaseAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [step, setStep] = useState('loading'); // loading | email | otp | receipts
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [showOTPModal, setShowOTPModal] = useState(false);

  const [receipts, setReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [receiptsError, setReceiptsError] = useState('');

  const centerId = config?.center_id || config?.id;
  const centerName = config?.centerName || config?.name || 'Center';
  const themeColor = config?.primaryColor || theme?.colors?.primary || '#1e4273';

  // Feature gate
  useEffect(() => {
    if (config && !isCommunityFeatureEnabled(config, 'tax_docs')) {
      navigate('/');
    }
  }, [config, navigate]);

  // Fetch receipts for the authenticated user
  const fetchReceipts = useCallback(async () => {
    if (!centerId) return;
    setReceiptsLoading(true);
    setReceiptsError('');
    try {
      const data = await taxDocsService.fetchReceipts(centerId);
      setReceipts(data.receipts || []);
      setStep('receipts');
    } catch (err) {
      console.error('[TaxDocs] Failed to fetch receipts:', err);
      if (err.message?.includes('Authentication')) {
        setStep('email');
      } else {
        setReceiptsError(err.message || 'Failed to load receipts');
        setStep('receipts');
      }
    } finally {
      setReceiptsLoading(false);
    }
  }, [centerId]);

  // Check auth state on mount
  useEffect(() => {
    if (!config) return;
    if (session && user?.email) {
      // Already authenticated — go directly to receipts
      fetchReceipts();
    } else {
      setStep('email');
    }
  }, [config, session, user, fetchReceipts]);

  // Send OTP code to email
  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setSending(true);
    setSendError('');

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          shouldCreateUser: true,
          data: {
            service: 'tax_docs',
            service_label: 'Tax Documents',
            center_name: centerName,
            center_logo: config?.logo_url || '',
            center_color: config?.colors?.primary?.replace('#', '') || '',
          }
        }
      });

      if (error) throw error;

      setShowOTPModal(true);
    } catch (err) {
      console.error('[TaxDocs] Failed to send OTP:', err);
      if (err.message?.includes('rate')) {
        setSendError('Too many attempts. Please wait a few minutes.');
      } else {
        setSendError(err.message || 'Failed to send verification code');
      }
    } finally {
      setSending(false);
    }
  };

  // OTP verified successfully
  const handleOTPSuccess = (newSession) => {
    console.log('[TaxDocs] OTP verified, session created');
    setShowOTPModal(false);
    // Prevent the Services Welcome Modal from showing for Tax Docs logins
    sessionStorage.removeItem('auth:justConfirmed');
    sessionStorage.removeItem('showWelcomeAfterConfirmation');
    // Session is now active — fetch receipts
    fetchReceipts();
  };

  // Sign out and go back to email entry
  const handleChangeEmail = async () => {
    try {
      await signOut();
    } catch (err) {
      console.error('[TaxDocs] Sign out error:', err);
    }
    setReceipts([]);
    setReceiptsError('');
    setEmail('');
    setStep('email');
  };

  // Build download URL from Google Drive view link
  const getDownloadUrl = (viewUrl) => {
    if (!viewUrl) return '#';
    // Convert /view?... to /export?format=pdf
    return viewUrl.replace(/\/view.*$/, '/export?format=pdf');
  };

  // Share receipt
  const handleShare = async (receipt) => {
    const shareData = {
      title: `Tax Receipt ${receipt.year}`,
      text: `Tax receipt for ${receipt.donor} - ${receipt.year}`,
      url: receipt.receiptUrl
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // User cancelled share
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(receipt.receiptUrl);
        alert('Receipt link copied to clipboard');
      } catch {
        // Ignore
      }
    }
  };

  // Don't render until config is loaded
  if (!config) return null;

  return (
    <div className="tax-docs-page" style={{ '--center-primary-color': themeColor }}>
      {/* Header */}
      <div className="tax-docs-header" style={{ background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}CC 100%)` }}>
        <h1>Tax Documents</h1>
        <p>Access your annual donation receipts</p>
      </div>

      {/* Step 1: Email Entry */}
      {step === 'email' && (
        <form className="tax-docs-email-card" onSubmit={handleSendCode}>
          <h2>Verify Your Email</h2>
          <p className="subtitle">
            Enter the email address associated with your donations to {centerName}
          </p>

          <input
            type="email"
            className="email-input"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (sendError) setSendError('');
            }}
            required
            autoFocus
            autoComplete="email"
          />

          {sendError && (
            <div style={{ color: '#c53030', fontSize: '13px', marginTop: '8px' }}>
              {sendError}
            </div>
          )}

          <button
            type="submit"
            className="tax-docs-send-btn"
            disabled={sending || !email.trim()}
          >
            {sending ? (
              <span className="tax-docs-sending">
                <span className="spinner-inline" />
                Sending code...
              </span>
            ) : (
              'Send Verification Code'
            )}
          </button>
        </form>
      )}

      {/* Step 2: OTP Modal */}
      <OTPVerificationModal
        show={showOTPModal}
        onHide={() => setShowOTPModal(false)}
        email={email}
        onSuccess={handleOTPSuccess}
        centerName={centerName}
      />

      {/* Step 3: Loading receipts */}
      {step === 'loading' && (
        <div className="tax-docs-skeleton">
          <div className="tax-docs-skeleton-card" />
          <div className="tax-docs-skeleton-card" />
          <div className="tax-docs-skeleton-card" />
        </div>
      )}

      {/* Step 3: Receipts */}
      {step === 'receipts' && (
        <>
          {receiptsLoading ? (
            <div className="tax-docs-skeleton">
              <div className="tax-docs-skeleton-card" />
              <div className="tax-docs-skeleton-card" />
              <div className="tax-docs-skeleton-card" />
            </div>
          ) : receiptsError ? (
            <div className="tax-docs-error">
              <p>{receiptsError}</p>
              <button
                className="tax-docs-send-btn"
                onClick={fetchReceipts}
                style={{ maxWidth: '200px', margin: '0 auto' }}
              >
                Retry
              </button>
            </div>
          ) : receipts.length === 0 ? (
            <div className="tax-docs-empty">
              <div className="empty-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <path d="M14 2v6h6" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                </svg>
              </div>
              <h3>No Receipts Found</h3>
              <p>
                No tax receipts were found for <strong>{user?.email}</strong>.
                Please make sure you are using the email address associated with your tax receipts.
                If you need help, please contact the center office.
              </p>
              <button
                className="tax-docs-send-btn"
                onClick={handleChangeEmail}
                style={{ maxWidth: '250px', margin: '16px auto 0' }}
              >
                Try a different email
              </button>
            </div>
          ) : (
            <div className="tax-docs-receipts-container">
              <div className="tax-docs-receipts-header">
                <h2>Your Tax Documents</h2>
                <span className="email-badge">{user?.email}</span>
              </div>

              {receipts.map((receipt, index) => (
                <div className="tax-docs-receipt-card" key={`${receipt.year}-${index}`}>
                  <div className="tax-docs-year-badge" style={{ background: themeColor }}>
                    {receipt.year}
                  </div>
                  <div className="tax-docs-receipt-info">
                    <p className="donor-name">{receipt.donor}</p>
                    <p className="receipt-label">Tax Receipt</p>
                  </div>
                  <div className="tax-docs-receipt-actions">
                    <a
                      href={receipt.receiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tax-docs-action-btn view"
                    >
                      View
                    </a>
                    <a
                      href={getDownloadUrl(receipt.receiptUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tax-docs-action-btn download"
                      title="Download PDF"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    </a>
                    <button
                      className="tax-docs-action-btn share"
                      onClick={() => handleShare(receipt)}
                      title="Share"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="18" cy="5" r="3" />
                        <circle cx="6" cy="12" r="3" />
                        <circle cx="18" cy="19" r="3" />
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Change email link */}
          <div className="tax-docs-change-email">
            <button onClick={handleChangeEmail}>
              Check a different email
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default TaxDocsPage;
